// ...existing code...
import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { Observable } from 'rxjs';
import { catchError, tap, map } from 'rxjs/operators';
import { DriveFile, GmailMessage, GoogleStatus } from '../models/google/google.model';
import { FrontendLoggerService } from './frontend-logger.service';


@Injectable({ providedIn: 'root' })
export class GoogleIntegrationService {
    constructor(
        private api: ApiService,
        private readonly frontendLogger: FrontendLoggerService
    ) { }

    exchangeCodeForToken(code: string, scope?: string, state?: string, redirectUri?: string): Observable<any> {
        const effectiveRedirect = redirectUri || `${window.location.origin}/callback`;
        const body: any = { code, redirectUri: effectiveRedirect };
        if (scope){ 
            body.scope = scope;
        }
        if (state) {
            body.state = state;
        }
        this.frontendLogger.debug(`[GoogleIntegrationService] exchangeCodeForToken body: ${JSON.stringify(this.sanitizeCallbackBody(body))}`);
            return this.api.post('google/callback', body).pipe(
                tap((res) => this.frontendLogger.info(`[GoogleIntegrationService] POST google/callback OK: ${JSON.stringify(res)}`)),
                catchError((error) => {
                    this.frontendLogger.error(`[GoogleIntegrationService] POST google/callback falló: ${JSON.stringify(error)}`);
                    throw error;
                })
        );
    }

    getAuthUrl(): Observable<{ authUrl: string }> {
        return this.api.get<{ authUrl: string }>('google/auth-url');
    }

    getStatus(): Observable<GoogleStatus> {
        return this.api.get<any>('google/status').pipe(
            map((res: any) => {
                if (res && res.connected && Array.isArray(res.accounts) && res.accounts.length > 0) {
                    return {
                        isConnected: true,
                        defaultAccountId : res.defaultAccountId || null,
                        accounts: res.accounts.map((email: string) => ({
                            avatar: res.pictureUrl || '',
                            name: res.name || '',
                            email,
                            default: email === res.defaultEmail,
                            tokenStatus: res.tokenStatus || '',
                            lastUpdate: res.syncProcess?.lastSyncDate || '',
                            scopes: res.scopes || [],
                            syncProcess: res.syncProcess || null,

                        })),
                        grantedScopes: res.scopes || [],
                        syncProcess: res.syncProcess || null
                    } as GoogleStatus;
                } else {
                    return {
                        isConnected: false,
                        accounts: [],
                        grantedScopes: [],
                        syncProcess: null
                    } as GoogleStatus;
                }
            })
        );
    }

    disconnect(): Observable<any> {
        return this.api.post('google/disconnect', {});
    }

    setDefaultAccount(email: string): Observable<any> {
        return this.api.post('google/set-default', { email });
    }

    reauthorize(email: string): Observable<{ authUrl: string }> {
        return this.api.post<{ authUrl: string }>('google/reauthorize', { email });
    }

    getGmailMessages(params: {
        unread?: boolean;
        fromDate?: string;
        label?: string;
    }): Observable<GmailMessage[]> {
        return this.api.get<GmailMessage[]>('google/gmail/messages', params).pipe(
            map((messages: any[]) => {
                return messages.map((msg) => ({
                    id: msg.id,
                    subject: msg.subject ?? msg.headers?.Subject ?? '',
                    from: msg.from ?? msg.headers?.From ?? '',
                    receivedDateTime: msg.receivedDateTime ?? null,
                    date: msg.date ?? msg.headers?.Date ?? '',
                    bodyPreview: msg.bodyPreview ?? null,
                    body: msg.body ?? '',
                    hasAttachments: msg.hasAttachments ?? (Array.isArray(msg.attachments) && msg.attachments.length > 0),
                    attachments: msg.attachments ?? [],
                    webLink: msg.webLink ?? null,
                }));
            })
        );
    }

    setGmailSyncPeriod(period: number, googleAccountId: string): Observable<any> {
        return this.api.post('google/gmail/gmail-sync-period', { Days: period, GoogleAccountId: googleAccountId });
    }

    setGmailSyncStartDate(startDate: string, googleAccountId: string): Observable<any> {
        return this.api.post('google/gmail/gmail-sync-period', { StartDate: startDate, GoogleAccountId: googleAccountId });
    }

    getDriveFiles(params: {
        folder?: string;
        ext?: string;
        maxSize?: number;
        size?: number;
    }): Observable<DriveFile[]> {
        return this.api.get<DriveFile[]>('google/drive/files', params);
    }

    private sanitizeCallbackBody(body: Record<string, unknown>): Record<string, unknown> {
        return {
            ...body,
            ...(body['code'] ? { code: '[REDACTED]' } : {}),
            ...(body['state'] ? { state: '[REDACTED]' } : {}),
            ...(body['scope'] ? { scope: '[REDACTED]' } : {}),
        };
    }
}
